/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import {
  Box,
  Button,
  Flex,
  IconTrash,
  JpegFileSVG,
  JpgSVG,
  PdfFileSVG,
  PngFileSVG,
  SquareBlock,
  UnknownFileSVG,
} from "@powerledger/ui-component-lib";
import { FC, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useViewDocument } from "@/app/components/document-popup/use-view-document";
import { ViewDocument } from "@/app/components/document-popup/view-document";
import { ModalContext } from "@/app/modal-provider";
import { AccountDocument, DocumentStatus, useRemoveDocumentMutation } from "@/app/types/generated/graphql";

import { DocumentBlockProps, MinDocumentStatus } from "./document-block.types";
import {
  DocumentStatusIconMap,
  getStatusText,
  mapDocumentState,
  requiredDocTypeIconMap,
  requiredDocTypeNameMap,
} from "./document-block-helpers";

function DeleteDocument({ documentId = "" }) {
  const { t } = useTranslation();
  const { showConfirmationModal } = useContext(ModalContext);
  const [removeDocument] = useRemoveDocumentMutation({
    refetchQueries: [AccountDocument],
  });
  return (
    <Button
      variant="text"
      onClick={() => {
        showConfirmationModal({
          onSubmit: async () => {
            await removeDocument({
              variables: {
                input: {
                  documentId,
                },
              },
            });
          },
          title: t("Are you sure you want to delete the document?"),
          messages: {
            successMessage: t("Document Deleted successfully"),
            errorMessage: t("Some errors occurred while deleting the document. Please try again after some time"),
          },
        });
      }}
      aria-label={t("Delete document {{id}}", documentId)}
    >
      <IconTrash stroke="error.500" size={6} />
    </Button>
  );
}

export const DocumentBlock: FC<DocumentBlockProps> = ({
  requiredDocument,
  noEdit = false,
  documentOf,
  showStatusText = false,
  titlePrefix,
  onUpload,
}) => {
  const { t } = useTranslation();

  const viewDocumentLogic = useViewDocument(requiredDocument);

  const documentState = mapDocumentState(requiredDocument, documentOf);

  const DocumentStateIcon = DocumentStatusIconMap[documentState];

  const documentActions = useMemo(() => {
    return (
      requiredDocument?.status === DocumentStatus.Uploaded && (
        <Flex sx={{ alignItems: "center", mt: 3, flexDirection: "column" }}>
          {DocumentStateIcon && showStatusText && (
            <DocumentStateIcon
              size={6}
              color={
                documentState === MinDocumentStatus.Approved
                  ? "success.500"
                  : documentState === MinDocumentStatus.Error
                  ? "errors.500"
                  : "warning.500"
              }
              data-place="bottom"
              data-tip={t(getStatusText(requiredDocument, documentOf))}
              sx={{ flexShrink: 0 }}
            />
          )}
          {!noEdit && (
            <Flex
              sx={{
                flexDirection: "column",
              }}
            >
              <Flex sx={{ fontSize: 0, justifyContent: "center", alignItems: "center", color: "warning.500" }}>
                {t("Remove the current document to reupload")}
              </Flex>
              <DeleteDocument documentId={requiredDocument.id} />
            </Flex>
          )}

          <ViewDocument {...viewDocumentLogic} />
        </Flex>
      )
    );
  }, [requiredDocument, DocumentStateIcon, showStatusText, documentState, viewDocumentLogic, t, documentOf, noEdit]);

  const fileIcon = useMemo(() => {
    switch (requiredDocument?.mediaType) {
      case "image/png":
        return <PngFileSVG />;
      case "image/jpg":
        return <JpgSVG />;
      case "image/jpeg":
        return <JpegFileSVG />;
      case "application/pdf":
        return <PdfFileSVG />;
      default:
        return <UnknownFileSVG />;
    }
  }, [requiredDocument?.mediaType]);

  const title = useMemo(() => {
    const text = `${titlePrefix?.trim()}'s ${requiredDocTypeNameMap[requiredDocument?.type || "ID_CARD"]}`;
    return requiredDocument?.status === DocumentStatus.Uploaded ? text : `Upload ${text} Document`;
  }, [requiredDocument?.status, requiredDocument?.type, titlePrefix]);

  const cardIcon = useMemo(() => {
    return requiredDocument?.status !== DocumentStatus?.Uploaded
      ? requiredDocTypeIconMap[requiredDocument?.type || "ID_CARD"]
      : fileIcon;
  }, [requiredDocument?.type, requiredDocument?.status, fileIcon]);

  return (
    <SquareBlock
      isEmpty={requiredDocument?.status !== DocumentStatus.Uploaded}
      onClick={onUpload}
      sx={{ mb: 3, px: 2, flexBasis: ["100%", null, null, "50%"], maxWidth: 300 }}
    >
      <Box sx={{ flexShrink: 0 }}>{cardIcon}</Box>
      <Box
        sx={{ textTransform: "uppercase", textAlign: "center", mt: 2, fontSize: 1 }}
        data-testid={`document-block--${titlePrefix?.trim().replace(/\s/g, "-") || "unknown"}`}
      >
        {t(title)}
      </Box>
      {documentActions}
    </SquareBlock>
  );
};
